// CP-836 slice 2 / CP-854 — shared pure helpers for the AI Review page. Kept out of index.js so
// the PDF document module can render exactly what the screen renders, and so both stay unit
// testable without mounting the page.

// FileID_YYYYMMDDHHMM naming from the mockup (the artifact contract).
export const evalFileName = audit => {
  const d = audit?.createdAt ? new Date(audit.createdAt) : null;
  if (!d || Number.isNaN(d.getTime())) return audit?.dqfId || '';
  const p = n => String(n).padStart(2, '0');
  const stamp = `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}${p(d.getHours())}${p(d.getMinutes())}`;
  return `${audit.dqfId}_${stamp}`;
};

// A malformed payload yields [], which reads as "No Issues found." both on screen and in the PDF.
// That is deliberate — the document must never disagree with the pane.
export const parseExceptions = audit => {
  if (!audit?.exceptions) return [];
  try {
    const parsed = JSON.parse(audit.exceptions);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

// CP-855 — the Qual List Available cell: Y/N plus the qualification the worker's cascade actually
// used, so a tester can match the id against the ID column on /qualification-list. Audits written
// before CP-855 have no qualListId and must keep rendering a bare 'Y' — the qualification used at
// the time is recorded nowhere, so there is nothing to backfill from.
export const qualListCell = audit => {
  if (audit?.qualListAvailable == null) return '';
  if (!audit.qualListAvailable) return 'N';
  return audit.qualListId ? `Y - ${audit.qualListId}` : 'Y';
};

// CP-867 — fold incoming evaluation records into the list on screen. Three callers deliver records
// now (the live subscription, the reconnect re-fetch, and the polling safety net), so this MUST be
// idempotent: the same record routinely arrives twice, once pushed and once polled, and applying it
// again has to be a no-op rather than a duplicate row.
//
// Fields are merged rather than replaced because a just-started audit is put in the list from the
// kick-off mutation's response, which returns fewer fields than the list query does — a bare
// overwrite would blank columns that were already populated.
//
// Records the caller does not yet know about are prepended, since the list is newest-first and the
// only way to meet an unknown record is for it to be newer than what is on screen.
export const mergeAudits = (prev, incoming) => {
  const list = Array.isArray(incoming) ? incoming.filter(r => r?.id) : [];
  if (!list.length) return prev;
  const merged = new Map(prev.map(a => [a.id, a]));
  const added = [];
  list.forEach(rec => {
    if (merged.has(rec.id)) {
      merged.set(rec.id, { ...merged.get(rec.id), ...rec });
    } else {
      merged.set(rec.id, rec);
      added.push(rec.id);
    }
  });
  return [...added.map(i => merged.get(i)), ...prev.map(a => merged.get(a.id))];
};
